import { useEffect, useRef, useState } from 'react';
import { usePhoneStore } from '../../stores/phone-store';
import { useAudioDevices } from '../../hooks/useAudioDevices';

type Step = 'welcome' | 'level' | 'denied' | 'done';

/**
 * First-run mic check: permission → live level meter (5s) → save default device.
 * Echo test is skipped to keep the flow short; DeviceSelector covers that.
 */
export function MicCheck({
  onComplete,
  onSkip,
}: {
  onComplete?: () => void;
  onSkip?: () => void;
}) {
  const [step, setStep] = useState<Step>('welcome');
  const [level, setLevel] = useState(0);
  const { refresh } = useAudioDevices();
  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const setMicrophone = usePhoneStore((s) => s.setMicrophone);

  useEffect(() => {
    return () => cleanup();
  }, []);

  function cleanup() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (ctxRef.current) {
      ctxRef.current.close().catch(() => {});
      ctxRef.current = null;
    }
  }

  async function runLevelCheck() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      refresh();

      const track = stream.getAudioTracks()[0];
      const settings = track?.getSettings();
      if (settings?.deviceId) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const mic = devices.find(
          (d) => d.deviceId === settings.deviceId && d.kind === 'audioinput',
        );
        const ref = {
          id: settings.deviceId,
          label: mic?.label ?? null,
        };
        // setMicrophone persisteert én past de keuze live toe (owner-aware).
        setMicrophone(ref);
      }

      const ctx = new AudioContext();
      ctxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const start = Date.now();

      const loop = () => {
        if (!ctxRef.current) return;
        analyser.getByteTimeDomainData(buf);
        let peak = 0;
        for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
        setLevel(peak / 128);
        if (Date.now() - start < 5000) {
          requestAnimationFrame(loop);
        } else {
          cleanup();
          setStep('done');
        }
      };

      setStep('level');
      loop();
    } catch (err) {
      console.warn('[MicCheck] permission denied', err);
      setStep('denied');
    }
  }

  if (step === 'welcome') {
    return (
      <div className="p-4 space-y-4">
        <h3 className="text-lg font-semibold">Microfoon instellen</h3>
        <p className="text-sm text-slate-600">
          We controleren snel of je microfoon werkt zodat je eerste gesprek
          soepel gaat. Dit duurt 5 seconden.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={runLevelCheck}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
          >
            Start test
          </button>
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Overslaan
            </button>
          )}
        </div>
      </div>
    );
  }

  if (step === 'level') {
    return (
      <div className="p-4 space-y-4">
        <h3 className="text-lg font-semibold">Spreek iets…</h3>
        <p className="text-sm text-slate-600">
          Zeg bijvoorbeeld &quot;test een twee drie&quot; — je ziet het volume
          hieronder oplichten.
        </p>
        <div
          className="h-3 bg-slate-200 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={Math.round(level * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Microfoon niveau"
        >
          <div
            className="h-full bg-green-500 transition-[width] duration-75"
            style={{ width: `${Math.min(100, level * 200)}%` }}
          />
        </div>
      </div>
    );
  }

  if (step === 'denied') {
    return (
      <div className="p-4 space-y-3">
        <h3 className="text-lg font-semibold">Geen toegang tot microfoon</h3>
        <p className="text-sm text-slate-600">
          Je browser blokkeert de microfoon. Sta toegang toe via het slotje in
          de adresbalk en probeer het opnieuw.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={runLevelCheck}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
          >
            Opnieuw proberen
          </button>
          {onSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            >
              Overslaan
            </button>
          )}
        </div>
      </div>
    );
  }

  // step === 'done'
  return (
    <div className="p-4 space-y-3">
      <h3 className="text-lg font-semibold text-green-700">Klaar!</h3>
      <p className="text-sm text-slate-600">
        Microfoon is geconfigureerd. Je kunt nu bellen.
      </p>
      <button
        type="button"
        onClick={() => onComplete?.()}
        className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
      >
        Sluiten
      </button>
    </div>
  );
}
